import { Config } from "@markdoc/markdoc";
import { document } from "./nodes/document";
import { Element } from "./nodes/Element";
import { File } from "./nodes/File";
import { FilesToExport } from "./nodes/FilesToExport";
import { FileToExportRow } from "./nodes/FileToExportRow";
import { HistoricalFileImportNotice } from "./nodes/HistoricalFileImportNotice";
import { InstructionAlert } from "./nodes/InstructionAlert";
import { link } from "./nodes/MarkdocLink";
import { ZipFileExtractionNotice } from "./nodes/ZipFileExtractionNotice";
import { alert } from "./tags/alert";
import { element } from "./tags/element";
import { file } from "./tags/file";
import { filesToExport, fileToExport } from "./tags/filesToExport";
import { historicalFileImportNotice } from "./tags/historicalFileImportNotice";
import { importAction } from "./tags/importAction";
import { instructionHeading } from "./tags/instructionHeading";
import { ipPermissions } from "./tags/ipPermissions";
import { limitationsHeading } from "./tags/limitationsHeading";
import { loginStep } from "./tags/loginStep";
import { stepsHeading } from "./tags/stepsHeading";
import { value } from "./tags/value";
import { zipFileExtractionNotice } from "./tags/zipFileExtractionNotice";

/**
 * Base MarkDoc configuration.
 */
export const markdocConfig: Config = {
  nodes: {
    document,
    link,
  },
};

/**
 * Markdoc extensions for data source instructions.
 *
 * Tags:
 * - `{% instructionHeading /%}` inserts the top-level instruction heading.
 * - `{% limitationsHeading /%}` inserts the heading for known limitations and issues.
 * - `{% stepsHeading /%}` inserts the heading for the steps.
 * - `{% loginStep /%}` inserts the standard log-in step, inserting the website URL where available.
 * - `{% filesToExport %}...{% /filesToExport %}` inserts a file export list.
 * - `{% fileToExport name="..." /%}` inserts a file in the file export list.
 * - `{% historicalFileImportNotice /%}` inserts a notice for data sources that are no longer operating but can still import historical files.
 * - `{% ipPermissions /%}` inserts Syla's API allowlist IP addresses as inline code.
 * - `{% importAction /%}` inserts the current import action as bold text.
 * - `{% element label="..." /%}` marks a visible UI element, such as a button, menu item, field, option in a dropdown etc.
 * - `{% value value="..." /%}` marks exact text the user should enter or copy into the UI.
 * - `{% alert %}...{% /alert %}` inserts an alert box.
 * - `{% zipFileExtractionNotice /%}` inserts a notice for data sources that may require a .zip file extraction.
 * - `{% file name="..." /%} insert an inline file reference
 *
 * Variables:
 * - `dataSourceName` is the current data source name.
 * - `dataSourceType` is the current data source type.
 * - `dataSourceWebsiteUrl` is the data source website URL, used by `{% loginStep /%}`.
 * - `importMethod` is used by some tags to control the displayed text.
 *
 * File import example:
 * ```md
 * {% instructionHeading /%}
 *
 * {% historicalFileImportNotice /%}
 *
 * {% filesToExport %}
 * {% fileToExport name="Sends/Receives" fileType="CSV" /%}
 * {% fileToExport name="Buys/Sells" fileType="CSV" /%}
 * {% fileToExport name="Affiliates" fileType="CSV" note="Optional. Only if you received affiliate rewards." /%}
 * {% /filesToExport %}
 *
 * {% stepsHeading /%}
 *
 * 1. {% loginStep /%}
 * 1. In the top right corner, click your profile icon, then select {% element label="API" href="https://www.coinspot.com.au/my/api" /%}.
 * 1. Click {% element label="Sends/Receives CSV" /%}.
 * 1. Click {% element label="Buys/Sells CSV" /%}.
 * 1. In the top right corner, click your profile icon, then select {% element label="Affiliates" /%}.
 * 1. Scroll down and click {% element label="Export CSV" /%}.
 * 1. Upload the {% file name="Sends/Receives" /%} and {% file name="Buys/Sells" /%} into Syla, then click {% importAction /%}.
 *
 * {% zipFileExtractionNotice /%}
 *
 * {% limitationsHeading /%}
 *
 * - AUD deposits and withdrawals, along with certain promotional giveaways and bonuses, are not included in the Sends/Receives CSV file. These can be manually entered based on the EOFY PDF statement, which can also be downloaded from CoinSpot.
 * ```
 *
 * API Sync example
 * ```md
 * {% instructionHeading /%}
 *
 * {% alert %}
 * This integration is for the separate Pro trading platform on CoinJar.
 * {% /alert %}
 *
 * {% stepsHeading /%}
 *
 * 1. {% loginStep /%}
 * 1. In the top right corner, click your profile icon, then select {% element label="API" /%}.
 * 1. Click {% element label="Generate New API Key" /%}.
 * 1. In {% element label="API Key Name" /%}, enter {% value value="Syla" /%}.
 * 1. Ensure {% element label="API Key Type" /%} is set to {% element label="Read Only" /%}.
 * 1. Enter your {% element label="Two Factor Code" /%}.
 * 1. Click {% element label="Create API Key" /%}.
 * 1. Find the email sent by CoinSpot and click the link to confirm the new API key.
 * 1. Copy the {% element label="API Key" /%} and paste it into Syla.
 * 1. Click {% element label="View Secret" /%}, then copy the {% element label="Secret" /%} and paste it into Syla.
 * 1. In Syla, click {% importAction /%}.
 *
 * {% limitationsHeading /%}
 *
 * - New transactions are delayed up to 24 hours before they appear in Syla.
 * - AUD payments made using the CoinSpot Mastercard and NFT transactions are not available from the API.
 * ```
 *
 */
export const dataSourceInstructionMarkdocConfig: Config = {
  tags: {
    instructionHeading,
    stepsHeading,
    limitationsHeading,
    ipPermissions,
    importAction,
    loginStep,
    element,
    value,
    filesToExport,
    fileToExport,
    alert,
    historicalFileImportNotice,
    zipFileExtractionNotice,
    file,
  },
};

export const dataSourceInstructionComponents = {
  FilesToExport,
  FileToExportRow,
  Element,
  InstructionAlert,
  HistoricalFileImportNotice,
  ZipFileExtractionNotice,
  File,
};
